import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Mail, RefreshCw, CheckCircle, XCircle, AlertCircle, 
  Settings, Play, Clock, Users, ArrowRight, ChevronDown,
  ChevronUp, Loader2, ToggleLeft, ToggleRight, Power
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Relationship statuses to map
const RELATIONSHIP_STATUSES = [
  { value: 'suspect', label: 'Suspect', description: 'New leads in first column' },
  { value: 'prospect', label: 'Prospect', description: 'Qualified leads with proposals' },
  { value: 'customer', label: 'Customer', description: 'Active paying customers' },
  { value: 'renewing', label: 'Renewing', description: 'Approaching contract renewal' },
  { value: 'paused', label: 'Paused', description: 'Temporarily inactive accounts' },
  { value: 'churned', label: 'Churned', description: 'Lost customers' }
];

export default function BrevoSettings() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [brevoLists, setBrevoLists] = useState([]);
  const [mappings, setMappings] = useState({});
  const [syncEnabled, setSyncEnabled] = useState(false);
  const [togglingSync, setTogglingSync] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [contactPreview, setContactPreview] = useState(null);
  const [error, setError] = useState(null);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      }
    } catch (err) {
      setError('Failed to check Brevo connection');
    }
  }, []);

  const fetchBrevoLists = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/lists`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBrevoLists(data.lists || []);
      }
    } catch (err) {
      setError('Failed to fetch Brevo lists');
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/config`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMappings(data.mappings || {});
        setSyncEnabled(data.sync_enabled || false);
      }
    } catch (err) {
      setError('Failed to fetch configuration');
    }
  }, []);

  const toggleSync = async () => {
    setTogglingSync(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/sync/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !syncEnabled })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncEnabled(data.sync_enabled);
        setSyncResult({ 
          type: 'success', 
          message: `Sync has been ${data.sync_enabled ? 'enabled' : 'disabled'}` 
        });
        setTimeout(() => setSyncResult(null), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to toggle sync');
      }
    } catch (err) {
      setError('Failed to toggle sync');
    } finally {
      setTogglingSync(false);
    }
  };

  const fetchSyncLogs = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/sync/logs?limit=10`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSyncLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch sync logs');
    }
  }, []);

  const fetchContactPreview = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/contacts/preview`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContactPreview(data);
      }
    } catch (err) {
      console.error('Failed to fetch contact preview');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchConnectionStatus(),
        fetchBrevoLists(),
        fetchConfig(),
        fetchSyncLogs(),
        fetchContactPreview()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchConnectionStatus, fetchBrevoLists, fetchConfig, fetchSyncLogs, fetchContactPreview]);

  const handleMappingChange = (status, listId, listName) => {
    setMappings(prev => ({
      ...prev,
      [status]: {
        list_id: listId ? parseInt(listId) : null,
        list_name: listName || null
      }
    }));
  };

  const saveMappings = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ mappings })
      });
      
      if (response.ok) {
        setSyncResult({ type: 'success', message: 'Configuration saved successfully!' });
        setTimeout(() => setSyncResult(null), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}/api/brevo/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ force: true })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSyncResult({
          type: data.result.status === 'completed' ? 'success' : 'warning',
          message: `Sync ${data.result.status}: ${data.result.synced_count} contacts synced`,
          details: data.result
        });
        fetchSyncLogs();
      } else {
        setError(data.detail || data.result?.errors?.[0] || 'Sync failed');
      }
    } catch (err) {
      setError('Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} p-6`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mail className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Brevo Integration
              </h1>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sync CRM contacts to Brevo email marketing lists
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className={`px-4 py-2 text-sm ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Connection Status Card */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {connectionStatus?.connected ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
              <div>
                <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Connection Status
                </h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {connectionStatus?.connected 
                    ? `Connected to ${connectionStatus.account?.company_name}`
                    : connectionStatus?.message || 'Not connected'}
                </p>
              </div>
            </div>
            <button
              onClick={fetchConnectionStatus}
              className={`p-2 rounded ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            >
              <RefreshCw className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>

        {/* Sync Toggle Card */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Power className={`w-6 h-6 ${syncEnabled ? 'text-green-500' : isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <div>
                <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Automatic Sync
                </h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {syncEnabled 
                    ? 'Daily sync is active (runs at 2:00 AM UTC)'
                    : 'Daily sync is currently disabled'}
                </p>
              </div>
            </div>
            <button
              onClick={toggleSync}
              disabled={togglingSync || !connectionStatus?.connected}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                syncEnabled
                  ? isDark 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                  : isDark 
                    ? 'bg-white/10 text-gray-400 hover:bg-white/20' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {togglingSync ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : syncEnabled ? (
                <ToggleRight className="w-5 h-5" />
              ) : (
                <ToggleLeft className="w-5 h-5" />
              )}
              {syncEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>

        {/* Contact Preview */}
        {contactPreview && (
          <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-6`}>
            <div className="flex items-center gap-2 mb-4">
              <Users className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Contacts to Sync
              </h2>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                ({contactPreview.total} total)
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(contactPreview.summary || {}).map(([status, count]) => (
                <div 
                  key={status}
                  className={`p-3 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                >
                  <div className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {count}
                  </div>
                  <div className={`text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {status}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Mapping */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-6`}>
          <div className="flex items-center gap-2 mb-4">
            <Settings className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
            <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Status to List Mapping
            </h2>
          </div>
          
          <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Map each CRM relationship status to a Brevo list. Contacts will be moved between lists when their status changes.
          </p>

          <div className="space-y-3">
            {RELATIONSHIP_STATUSES.map((status) => (
              <div 
                key={status.value}
                className={`flex items-center gap-4 p-3 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {status.label}
                  </div>
                  <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {status.description}
                  </div>
                </div>
                <ArrowRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <select
                  value={mappings[status.value]?.list_id || ''}
                  onChange={(e) => {
                    const selectedList = brevoLists.find(l => l.id === parseInt(e.target.value));
                    handleMappingChange(status.value, e.target.value, selectedList?.name);
                  }}
                  className={`w-64 px-3 py-2 rounded border text-sm ${
                    isDark 
                      ? 'bg-black border-white/20 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:border-[#A2FF00]`}
                >
                  <option value="">Select a list...</option>
                  {brevoLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name} ({list.totalSubscribers} contacts)
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
            <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              <Clock className="w-4 h-4 inline mr-1" />
              Auto-sync runs daily at 2:00 AM UTC
            </div>
            <button
              onClick={saveMappings}
              disabled={saving}
              className={`px-4 py-2 rounded font-medium text-sm ${
                isDark 
                  ? 'bg-[#A2FF00] hover:bg-[#8EFF00] text-black' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } disabled:opacity-50`}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>

        {/* Manual Sync */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg p-6`}>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Play className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                <h2 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Manual Sync
                </h2>
              </div>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sync all CRM contacts to Brevo immediately
              </p>
            </div>
            <button
              onClick={triggerSync}
              disabled={syncing || !connectionStatus?.connected}
              className={`px-6 py-2.5 rounded font-medium flex items-center gap-2 ${
                isDark 
                  ? 'bg-[#A2FF00] hover:bg-[#8EFF00] text-black' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              } disabled:opacity-50`}
            >
              {syncing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Sync Now
                </>
              )}
            </button>
          </div>

          {/* Sync Result */}
          {syncResult && (
            <div className={`mt-4 p-4 rounded ${
              syncResult.type === 'success' 
                ? 'bg-green-500/10 border border-green-500/20' 
                : 'bg-yellow-500/10 border border-yellow-500/20'
            }`}>
              <div className="flex items-center gap-2">
                {syncResult.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                )}
                <span className={syncResult.type === 'success' ? 'text-green-500' : 'text-yellow-500'}>
                  {syncResult.message}
                </span>
              </div>
              {syncResult.details && (
                <div className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div>Total contacts: {syncResult.details.total_contacts}</div>
                  <div>Added to lists: {syncResult.details.added_count}</div>
                  <div>Removed from lists: {syncResult.details.removed_count}</div>
                  {syncResult.details.errors?.length > 0 && (
                    <div className="text-red-400 mt-1">
                      Errors: {syncResult.details.errors.length}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-4 rounded bg-red-500/10 border border-red-500/20">
            <div className="flex items-center gap-2 text-red-500">
              <XCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Sync History */}
        <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-lg`}>
          <button
            onClick={() => setShowLogs(!showLogs)}
            className={`w-full p-4 flex items-center justify-between ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-2">
              <Clock className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Sync History
              </span>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                ({syncLogs.length} recent)
              </span>
            </div>
            {showLogs ? (
              <ChevronUp className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
            ) : (
              <ChevronDown className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
            )}
          </button>
          
          {showLogs && (
            <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
              {syncLogs.length === 0 ? (
                <div className={`p-4 text-center ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  No sync history yet
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {syncLogs.map((log, index) => (
                    <div key={index} className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {log.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : log.status === 'completed_with_errors' ? (
                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {log.sync_type === 'scheduled' ? 'Scheduled Sync' : 'Manual Sync'}
                          </span>
                        </div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {formatDate(log.timestamp)}
                        </span>
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {log.synced_count} of {log.total_contacts} contacts synced
                        {log.errors?.length > 0 && (
                          <span className="text-red-400 ml-2">
                            ({log.errors.length} errors)
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
